/**
 * Die Typen, die die Oberfläche vom Server erwartet.
 *
 * Spiegel von `server/src/domain/types.ts`. Bewusst als Kopie und nicht als
 * gemeinsames Paket: Der Server läuft auf Node, die Oberfläche im Browser,
 * und ein geteiltes Paket zwischen beiden kostet mehr Build-Werkzeug, als es
 * bei dieser Größe einbringt. Ändert sich das Domänenmodell, ändern sich beide
 * Dateien — der Typecheck auf beiden Seiten macht das sofort sichtbar.
 */

export type PruefStatus = 'offen' | 'geprueft' | 'rueckfrage';
export type PruefungStatus = 'vorbereitung' | 'freigegeben' | 'abgeschlossen';
export type Rolle = 'beirat' | 'verwaltung';
export type Zahlstatus = 'bezahlt' | 'teilweise_bezahlt' | 'nicht_bezahlt' | 'nicht_zahlbar';

export interface PdfRechteck {
  seite: number;
  x: number;
  y: number;
  breite: number;
  hoehe: number;
}

export interface Buchungszeile {
  id: string;
  belegId: string;
  sachkontoNummer: string | null;
  sachkontoBezeichnung: string | null;
  text: string;
  buchungsart: 'DEBIT' | 'CREDIT';
  betragsart: 'TOTAL' | 'NET';
  betrag: number;
  netto: number | null;
  ustBetrag: number | null;
  brutto: number | null;
  ustProzent: number | null;
  leistungVon: string | null;
  leistungBis: string | null;
  kostenstellen: { nummer: string; bezeichnung: string; betrag: number }[];
}

export interface Beleg {
  id: string;
  pruefungId: string;
  lieferant: string | null;
  rechnungsnummer: string | null;
  rechnungsdatum: string | null;
  faelligkeit: string | null;
  netto: number | null;
  brutto: number | null;
  ustProzent: number | null;
  waehrung: string;
  gutschrift: boolean;
  zahlstatus: Zahlstatus;
  zahldatum: string | null;
  offenerBetrag: number | null;
  seitenzahl: number;
  kategorieBezeichnung: string | null;
  buchungszeilen: Buchungszeile[];
  pruefstatus: PruefStatus;
  pruefstatusVon: string | null;
  pruefstatusAm: string | null;
  /** Anzeigedrehung des Belegbildes in Grad. Gehört zum Dokument, nicht zum Betrachter. */
  drehung: 0 | 90 | 180 | 270;
}

/** Eine Gruppe der Gliederung: ein Sachkonto mit seinen Belegen. */
export interface Gruppe {
  laufnummer: number;
  sachkonto: string;
  bezeichnung: string;
  /** Die Sammelgruppe am Ende: Belege, die niemand kontiert hat. */
  ohneKontierung: boolean;
  belegIds: string[];
  summe: number;
  geprueft: number;
  rueckfragen: number;
}

export interface Pruefung {
  id: string;
  liegenschaft_bez: string;
  bezeichnung: string;
  periode_von: string;
  periode_bis: string;
  status: PruefungStatus;
  frist: string | null;
  urteil: string | null;
  freigegeben_am?: string | null;
  abgeschlossen_am?: string | null;
  gesamtanmerkung?: string | null;
  /** Woher Belege und Buchungen kommen. */
  belegquelle?: 'idwell' | 'import';
  mandant_id?: string | null;
}

// ─────────────────────────────────────────────────────────────
// Die fünf Prüfschritte
// ─────────────────────────────────────────────────────────────

export type SchrittStatus = 'offen' | 'laufend' | 'erledigt';
export type Werkzeug = 'salden' | 'unterlagen' | 'belege' | 'abschluss';

export interface Schritt {
  nummer: 1 | 2 | 3 | 4 | 5;
  titel: string;
  kurz: string;
  beschreibung: string;
  /** Welche Unterlagen üblicherweise dazugehören — Hinweis, keine Pflicht. */
  ueblich: string;
  werkzeug: Werkzeug;
  status: SchrittStatus;
  anmerkung: string | null;
  begonnenAm: string | null;
  erledigtVon: string | null;
  erledigtAm: string | null;
}

// ─────────────────────────────────────────────────────────────
// Unterlagen
// ─────────────────────────────────────────────────────────────

export interface Kategorie {
  id: string;
  mandantId: string;
  schluessel: string;
  bezeichnung: string;
  hinweis: string | null;
  schritt: number;
  reihenfolge: number;
  pflicht: boolean;
  aktiv: boolean;
}

export interface Dokument {
  id: string;
  kategorieId: string;
  dateiname: string;
  groesse: number;
  typ: string;
  seiten: number | null;
  hochgeladenVon: string | null;
  hochgeladenAm: string;
  pruefstatus: PruefStatus;
  pruefstatusVon: string | null;
  pruefstatusAm: string | null;
  /** Prüfvermerk des Beirats — erscheint im Protokoll. */
  vermerk: string | null;
  /** Haken in Schritt 1: Die Buchungen dieser Unterlage speisen den Belegabgleich. */
  abgleichQuelle?: boolean;
  abgleichAnzahl?: number;
}

export type Dokumentart = 'unterlage' | 'importpaket' | 'journal' | 'einzelabrechnungen' | 'mieter' | 'unbekannt';

export interface Saldenfund {
  konto: string;
  iban: string | null;
  anfang: number | null;
  ende: number | null;
  seite: 'auszug' | 'abrechnung';
}

/** Eine Datei im Eingang mit dem Vorschlag der Erkennung. */
export interface Eingangsposten {
  id: string;
  dateiname: string;
  quellePfad: string;
  typ: string;
  groesse: number;
  seiten: number | null;
  analyse: {
    erkennung: {
      art: Dokumentart;
      schluessel: string | null;
      sicherheit: number;
      grund: string;
      quelle: 'regel' | 'dateiname' | 'ki';
      salden: Saldenfund[];
    };
    kategorieId: string | null;
    ableitung: { einzelabrechnungen: number; hinweise: string[]; geldkonten: Saldenfund[]; bloecke: unknown[] } | null;
    vermutlichScan: boolean;
  };
  erstelltAm: string;
}

export type EingangAktion = 'unterlage' | 'importpaket' | 'journal' | 'ableiten' | 'zurueckhalten' | 'verwerfen';

export interface Saldenzeile {
  id: string;
  konto: string;
  iban: string | null;
  anfangAuszug: number | null;
  endeAuszug: number | null;
  anfangAbrechnung: number | null;
  endeAbrechnung: number | null;
  anfangStimmt: boolean | null;
  endeStimmt: boolean | null;
  geaendertVon: string | null;
  geaendertAm: string | null;
}

// ─────────────────────────────────────────────────────────────
// Verwaltungsseite
// ─────────────────────────────────────────────────────────────

export interface Mandant {
  id: string;
  name: string;
  kurzname: string | null;
  idwellBasisUrl: string | null;
  idwellVerbunden: boolean;
  schluesselSeit: string | null;
  aktiv: boolean;
  erstelltAm: string;
}

export interface Benutzer {
  id: string;
  email: string;
  name: string;
  systemadmin: boolean;
}

export interface Konto {
  benutzer: Benutzer;
  mandant: Mandant | null;
  /** Nur beim Betreiber: alle Hausverwaltungen. */
  mandanten?: Mandant[];
  mailBereit: boolean;
}

export interface Mitarbeiter {
  id: string;
  email: string;
  name: string;
  aktiv: number;
  erstelltAm: string;
  letzteAnmeldungAm: string | null;
  passwortGesetzt: number;
}

export interface PruefungKurz {
  id: string;
  liegenschaftBezeichnung: string;
  bezeichnung: string;
  periodeVon: string;
  periodeBis: string;
  status: PruefungStatus;
  belegquelle: 'idwell' | 'import';
  frist: string | null;
  urteil: string | null;
  erstelltAm: string;
  beiraete: number;
  offeneRueckfragen: number;
  belege: number;
  dokumente: number;
  schritteErledigt: number;
  fortschritt: number;
}

export interface Mitglied {
  id: string;
  name: string;
  email: string;
  rolle: Rolle;
  gueltigBis: string;
  eingeladenAm: string;
  letzterZugriffAm: string | null;
}

export interface Kennzahlen {
  belegeGesamt: number;
  belegeGeprueft: number;
  belegeRueckfrage: number;
  belegeOffen: number;
  pruefsumme: number;
  rueckfragenOffen: number;
  transaktionenGesamt: number;
  transaktionenZugeordnet: number;
  abgleichquote: number;
  nichtZugeordneterBetrag: number;
  zahldatumAbweichungen: number;
  belegeOhneZahlung: number;
  transaktionenGeprueft: number;
  journalVorhanden: boolean;
  journalZeilen: number;
  journalSumme: number;
  journalKontrolleStimmt: boolean;
  journalBefunde: number;
  journalVorschlaege: number;
  schritteGesamt: number;
  schritteErledigt: number;
  fortschritt: number;
  dokumenteGesamt: number;
  dokumenteGeprueft: number;
}

// ─────────────────────────────────────────────────────────────
// Journal
// ─────────────────────────────────────────────────────────────

export interface Journalzeile {
  id: string;
  laufnummer: number;
  seite: number;
  datum: string;
  belegnummer: string;
  text: string;
  kontoSoll: string;
  kontoHaben: string;
  ustProzent: number | null;
  betrag: number;
}

export interface Journalzuordnung {
  id: string;
  zeileId: string;
  /** Null heißt: Es wurde festgehalten, dass es zu dieser Buchung keinen Beleg gibt. */
  belegId: string | null;
  status: 'zugeordnet' | 'vorschlag' | 'ohne_beleg';
  konfidenz: number;
  quelle: 'automatisch' | 'manuell' | 'idwell' | 'ki';
  begruendung: string | null;
}

export type BefundArt =
  | 'buchung_ohne_beleg'
  | 'beleg_ohne_buchung'
  | 'betrag_weicht_ab'
  | 'kontierung_weicht_ab';

export interface Befund {
  art: BefundArt;
  zeilenIds: string[];
  belegId: string | null;
  text: string;
  betrag: number;
}

/**
 * Was das Journal über sich selbst sagt, und was gelesen wurde.
 *
 * Beides nebeneinander statt verrechnet: Gehen sie auseinander, muss die
 * Richtung sichtbar sein.
 */
export interface Journalkontrolle {
  anzahlLautDokument: number | null;
  summeLautDokument: number | null;
  anzahlGelesen: number;
  summeGelesen: number;
  stimmt: boolean;
}

export interface Journal {
  vorhanden: boolean;
  dateiname?: string;
  liegenschaftNummer?: string | null;
  liegenschaftBezeichnung?: string | null;
  periodeVon?: string | null;
  periodeBis?: string | null;
  kontrolle?: Journalkontrolle;
  /** Das Konto, über das die Zahlungen laufen — aus dem Ausdruck erschlossen. */
  zahlungskonto?: string | null;
  hinweise?: string[];
  importiertAm?: string;
  importiertVon?: string | null;
  zeilen?: Journalzeile[];
  zuordnungen?: Journalzuordnung[];
  befunde?: Befund[];
  summeJournal?: number;
  summeBelege?: number;
}

export interface Banktransaktion {
  id: string;
  kontoIban: string | null;
  waehrung: string;
  buchungsdatum: string;
  valutadatum: string | null;
  betrag: number;
  richtung: 'eingang' | 'ausgang';
  partnerName: string | null;
  partnerIban: string | null;
  verwendungszweck: string | null;
  referenz: string | null;
  herkunft: 'camt053' | 'camt052' | 'camt054' | 'mt940' | 'pdf' | 'idwell' | 'csv' | 'json' | 'xml';
  /** Die hochgeladene Unterlage, aus der die Buchung stammt, und die Stelle darin. */
  quelleDokumentId?: string | null;
  quellePosition?: PdfRechteck | null;
}

/**
 * Zuordnung einer Zahlung zu einem Beleg.
 *
 * Feldnamen wie im Server: der Endpunkt übersetzt die Datenbankzeilen ins
 * Domänenmodell, bevor er antwortet. Die Kommentare weiter unten kommen
 * dagegen als rohe Zeilen und bleiben deshalb in Unterstrich-Schreibweise —
 * ein Unterschied, der hier sichtbar bleiben soll statt stillschweigend
 * vermischt zu werden.
 */
export interface Zuordnung {
  id: string;
  transaktionId: string;
  belegId: string;
  betrag: number;
  status: 'zugeordnet' | 'vorschlag' | 'ohne_beleg' | 'nicht_belegpflichtig';
  konfidenz: number;
  quelle: 'automatisch' | 'manuell' | 'idwell';
  begruendung: string | null;
}

export interface Kommentar {
  id: string;
  beleg_id: string | null;
  transaktion_id: string | null;
  dokument_id?: string | null;
  schritt?: number | null;
  eltern_id: string | null;
  autor_name: string;
  autor_rolle: Rolle;
  text: string;
  status: 'offen' | 'beantwortet' | 'erledigt';
  /** Markierte Stelle im Beleg oder in der Unterlage, als JSON (PdfRechteck). */
  markierung?: string | null;
  erstellt_am: string;
}

/** Die markierte Stelle einer Rückfrage, falls sie eine hat. */
export function markierungLesen(k: Pick<Kommentar, 'markierung'>): PdfRechteck | null {
  if (!k.markierung) return null;
  try {
    const m = typeof k.markierung === 'string' ? (JSON.parse(k.markierung) as Partial<PdfRechteck>) : (k.markierung as Partial<PdfRechteck>);
    if (typeof m.x === 'number' && typeof m.y === 'number' && typeof m.breite === 'number' && typeof m.hoehe === 'number') {
      return { seite: typeof m.seite === 'number' ? m.seite : 1, x: m.x, y: m.y, breite: m.breite, hoehe: m.hoehe };
    }
  } catch {
    // kein gültiges Rechteck
  }
  return null;
}

/**
 * Was der Beirat über die Bezahlung eines Belegs wissen will.
 *
 * Beide Daten stehen nebeneinander — aus der Buchhaltung und aus dem
 * Kontoauszug — statt eines zusammengeführten Wertes, der die Herkunft
 * verwischt.
 */
export interface Zahlungsbefund {
  belegId: string;
  zahldatumLautBuchhaltung: string | null;
  zahldatumLautKontoauszug: string | null;
  /** Tage zwischen beiden. Negativ: der Kontoauszug ist früher. */
  abweichungTage: number | null;
  zugeordneterBetrag: number;
  offen: number | null;
  /** Für den Sprung in den Kontoauszug. */
  transaktionIds: string[];
  /** Unbestätigte Vorschläge des Abgleichs — kein Zahlungsnachweis, aber eine Spur. */
  vorschlagIds?: string[];
  status:
    | 'bezahlt'
    | 'teilweise'
    | 'keine_zahlung'
    | 'datum_weicht_ab'
    | 'kein_kontoauszug';
}

/** Die Antwort von /pruefungen/:id/transaktionen. */
export interface Kontoauszug {
  transaktionen: Banktransaktion[];
  zuordnungen: Zuordnung[];
  /** Position → Belege, die sie bezahlt. Sammelüberweisungen haben mehrere. */
  belegeJePosition: Record<string, string[]>;
  /** Abbuchungen, zu denen kein Beleg gefunden wurde. */
  ohneBeleg: string[];
  /** Prüfstatus je Position. Fehlt ein Eintrag, gilt "offen". */
  status: Record<string, { status: PruefStatus; von: string | null; am: string | null }>;
}

export type Hervorhebungen = Record<string, PdfRechteck>;

/** Ein Beleg, im Kontoauszug-PDF wiedergefunden — Seite und Kasten zum Anzeigen. */
export interface Fundstelle {
  dokumentId: string;
  dateiname: string;
  stelle: PdfRechteck;
  text: string;
  datum: string | null;
  konfidenz: number;
  gruende: string[];
}
